import React, { useEffect, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { Eye, EyeOff, Key } from "react-feather";
import { C } from "../reporting/reportingStyles";
import { changeOwnPassword, resetUserPassword } from "../../utils/apiCalls";

// Same rule as the API (src/util/passwordRules.js); the server is the real check.
const PASSWORD_RE = /^[a-zA-Z0-9!@#$%^&*()_+\-=.]{8,32}$/;
const RULE_HINT = "8–32 characters: letters, numbers and !@#$%^&*()_+-=.";

const labelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: C.textLight,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 6,
  display: "block",
};
const inputStyle = {
  padding: "10px 40px 10px 12px",
  fontSize: 13,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  outline: "none",
  color: C.text,
  background: C.white,
  width: "100%",
};

const PasswordInput = ({ label, value, onChange, autoComplete, autoFocus }) => {
  const [shown, setShown] = useState(false);
  const Icon = shown ? EyeOff : Eye;
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      <div style={{ position: "relative" }}>
        <input
          type={shown ? "text" : "password"}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
        <Icon
          size={16}
          color={C.textLight}
          onClick={() => setShown((s) => !s)}
          style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", cursor: "pointer" }}
        />
      </div>
    </div>
  );
};

/**
 * mode "reset":  system admin sets a new password for `user` (no current password needed).
 * mode "change": the signed-in user changes their own password.
 * Either way the API signs the account out of every other session.
 */
const PasswordModal = ({ isOpen, onClose, mode, user }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrent("");
      setNext("");
      setConfirm("");
    }
  }, [isOpen]);

  const isReset = mode === "reset";
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "this user";

  const error = !next
    ? null
    : !PASSWORD_RE.test(next)
      ? RULE_HINT
      : confirm && confirm !== next
        ? "Passwords do not match"
        : null;
  const canSubmit = (isReset || current) && next && confirm === next && PASSWORD_RE.test(next) && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    const ok = isReset ? await resetUserPassword(user.id, next) : await changeOwnPassword(current, next);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalBody style={{ padding: 24, fontFamily: "'Inter', sans-serif" }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: C.coralLight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Key size={18} color={C.coral} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
                {isReset ? "Reset password" : "Change password"}
              </div>
              <div style={{ fontSize: 12, color: C.textMed, overflow: "hidden", textOverflow: "ellipsis" }}>
                {isReset ? name : "Other devices will be signed out"}
              </div>
            </div>
          </div>

          {!isReset && (
            <PasswordInput
              label="Current password"
              value={current}
              onChange={setCurrent}
              autoComplete="current-password"
              autoFocus
            />
          )}
          <PasswordInput
            label="New password"
            value={next}
            onChange={setNext}
            autoComplete="new-password"
            autoFocus={isReset}
          />
          <PasswordInput label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" />

          <div style={{ fontSize: 11.5, color: error ? C.orange : C.textLight, minHeight: 18, marginTop: -4 }}>
            {error || RULE_HINT}
          </div>
          {isReset && (
            <div
              style={{
                fontSize: 12,
                color: C.text,
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: "8px 12px",
                marginTop: 12,
              }}
            >
              {name} will be signed out on every device (POS, apps, panel).
            </div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: C.white,
                border: `1.5px solid ${C.border}`,
                borderRadius: 8,
                padding: "9px 20px",
                fontWeight: 600,
                color: C.text,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                border: "none",
                borderRadius: 8,
                padding: "9px 20px",
                fontWeight: 600,
                color: C.white,
                fontSize: 13,
                cursor: canSubmit ? "pointer" : "not-allowed",
                opacity: canSubmit ? 1 : 0.55,
              }}
            >
              {saving ? "Saving…" : isReset ? "Reset password" : "Change password"}
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default PasswordModal;
